import { resolvePreferredCiDisplayStatus } from '@/features/shared/utils/ciStatus';
import type { PipelineStatusKey } from '@/features/quality/components/qualityStatusDataHelpers';
import type { QualityRange } from '@/features/quality/hooks';
import type { CiDisplayStatusKey, PipelineRunSummaryItemDto } from '@flowqa/shared';

export const toPipelineRunDisplayStatusFilters = (
  statuses: PipelineStatusKey[],
): CiDisplayStatusKey[] =>
  Array.from(new Set(statuses.filter((status): status is CiDisplayStatusKey => status !== 'none')));

export const shouldUseWindowedPipelineStatusFiltering = (
  range: QualityRange,
  statuses: PipelineStatusKey[],
): boolean => statuses.length > 0 && range !== 'latest' && !statuses.includes('none');

export const collectPipelineIdsFromRunSummaries = (
  runSummaries: readonly PipelineRunSummaryItemDto[],
): string[] =>
  Array.from(new Set(runSummaries.map((runSummary) => runSummary.pipelineId))).sort((left, right) =>
    left.localeCompare(right),
  );

export const resolvePipelineRunDisplayStatus = (
  runSummary: Pick<PipelineRunSummaryItemDto, 'status' | 'lifecycleState' | 'conclusion'>,
): CiDisplayStatusKey => {
  const preferredStatus = resolvePreferredCiDisplayStatus({
    status: runSummary.status,
    lifecycleState: runSummary.lifecycleState,
    conclusion: runSummary.conclusion,
  });

  if (preferredStatus === 'completed') {
    return 'success';
  }
  if (preferredStatus === 'waiting') {
    return 'queued';
  }
  if (preferredStatus === 'running') {
    return 'running';
  }
  if (
    preferredStatus === 'success' ||
    preferredStatus === 'warning' ||
    preferredStatus === 'failed' ||
    preferredStatus === 'canceled' ||
    preferredStatus === 'skipped' ||
    preferredStatus === 'neutral' ||
    preferredStatus === 'action_required' ||
    preferredStatus === 'timed_out' ||
    preferredStatus === 'unknown' ||
    preferredStatus === 'queued'
  ) {
    return preferredStatus;
  }

  if (runSummary.status === 'completed') {
    return 'success';
  }
  if (runSummary.status === 'failed') {
    return 'failed';
  }
  if (runSummary.status === 'running') {
    return 'running';
  }
  return 'queued';
};

export const filterRunSummariesByDisplayStatuses = (
  runSummaries: readonly PipelineRunSummaryItemDto[],
  statuses: readonly PipelineStatusKey[],
): PipelineRunSummaryItemDto[] => {
  if (statuses.length === 0) {
    return [...runSummaries];
  }

  const statusSet = new Set(statuses);
  return runSummaries.filter((runSummary) => {
    return statusSet.has(resolvePipelineRunDisplayStatus(runSummary));
  });
};
